/**
 * @file src/shell/InfoPanel.js
 * Panel "Thông tin bài giảng".
 *
 * QUAN TRỌNG: tiêu đề / mô tả / mục tiêu / tham chiếu SGK KHÔNG nằm trong tiết.
 * Chúng lấy từ content/index/<môn>.json do ADMIN quản lý — vì cùng một mô tả
 * cần xuất hiện ở 3 nơi (panel này, trang front-end để SEO, catalog). Một nguồn
 * duy nhất ⇒ không bao giờ lệch, và sửa mô tả không cần dev.
 *
 * Tiết chỉ THÊM mục đặc thù qua ctx.ui.addInfoSection().
 */

import { icon } from './icons.js';

/**
 * @param {Object} o
 * @param {HTMLElement} o.root
 * @param {(k: string, v?: any) => string} o.t
 * @param {Record<string, any>} o.info
 * @param {() => string} o.lang
 * @param {import('./registry.js').Registry<any>} o.sections
 * @param {(open: boolean) => void} [o.onOpenChange]
 */
export function createInfoPanel(o) {
  const el = document.createElement('aside');
  el.className = 'ol-info';
  el.innerHTML = `
    <div class="ol-info__head">
      <span class="ol-info__title"></span>
      <button class="ol-btn ol-btn--icon ol-info__close"><span class="ol-icon">${icon('close')}</span></button>
    </div>
    <div class="ol-info__body"></div>`;
  o.root.appendChild(el);
  // Sinh ra là đang ĐÓNG → inert ngay, không chờ close() lần đầu.
  el.inert = true;

  const body = /** @type {HTMLElement} */ (el.querySelector('.ol-info__body'));
  const head = /** @type {HTMLElement} */ (el.querySelector('.ol-info__title'));
  /** @type {HTMLElement} */ (el.querySelector('.ol-info__close')).addEventListener('click', () =>
    close(),
  );

  function render() {
    const L = o.lang();
    head.textContent = o.t('info.panelTitle');
    body.innerHTML = '';

    const pick = (/** @type {any} */ v) =>
      v && typeof v === 'object' ? (v[L] ?? v.vi ?? '') : (v ?? '');

    // ----- Phần từ content/index (admin quản) -----
    const title = pick(o.info?.title);
    if (title) body.appendChild(h('h3', title));

    const desc = pick(o.info?.description);
    if (desc) body.appendChild(h('p', desc));

    const objectives = o.info?.objectives ?? [];
    if (objectives.length) {
      body.appendChild(h('h4', o.t('info.objectives')));
      const ul = document.createElement('ul');
      for (const ob of objectives) ul.appendChild(h('li', pick(ob.label)));
      body.appendChild(ul);
    }

    if (o.info?.textbookRef) {
      body.appendChild(h('p', o.info.textbookRef, 'ol-info__ref'));
    }

    // ----- Mục do TIẾT thêm -----
    for (const slot of o.sections.list()) {
      const d = slot.data;
      body.appendChild(h('h4', o.t(d.titleKey)));
      body.appendChild(h('p', o.t(d.bodyKey)));
    }
  }

  // onOpenChange: shell dùng để tô sáng nút "thông tin" ở taskbar đúng theo panel.
  // Phải phát từ ĐÂY chứ không phải chỗ gọi, vì panel còn tự đóng bằng dấu × bên trong —
  // shell không thể biết được nếu chỉ nhìn lời gọi toggle().
  const emitOpen = () => o.onOpenChange?.(el.classList.contains('is-open'));
  // inert khi ĐÓNG — xem chú thích cùng nội dung trong SettingsPanel.js.
  const open = () => {
    render();
    el.inert = false;
    el.classList.add('is-open');
    emitOpen();
  };
  const close = () => {
    el.classList.remove('is-open');
    el.inert = true;
    emitOpen();
  };
  const toggle = () => (el.classList.contains('is-open') ? close() : open());

  const off = o.sections.onChange(() => {
    if (el.classList.contains('is-open')) render();
  });
  render();

  return {
    el,
    open,
    close,
    toggle,
    isOpen: () => el.classList.contains('is-open'),
    retranslate: render,
    dispose() {
      off();
      el.remove();
    },
  };
}

/**
 * @param {string} tag @param {string} text @param {string} [cls]
 */
function h(tag, text, cls) {
  const e = document.createElement(tag);
  e.textContent = text;
  if (cls) e.className = cls;
  return e;
}
